import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';
import { fetchReceitaById, updateReceita, fetchContasReceita, deleteReceita } from '../../../services/receitaService';
import ConfirmModal from '../../../components/ConfirmModal';

// Função para converter data ISO para formato YYYY-MM-DD (compatível com input date)
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  try {
    console.log('Formatando data:', dateString, typeof dateString);
    
    // Caso específico: formato ISO com T00:00:00.000Z
    if (typeof dateString === 'string' && dateString.includes('T00:00:00.000Z')) {
      const [datePart] = dateString.split('T');
      console.log('Data formatada (caso específico T00:00:00.000Z):', datePart);
      return datePart;
    }
    
    // Caso específico: formato ISO com T e hora
    if (typeof dateString === 'string' && dateString.includes('T') && dateString.includes('Z')) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formatted = `${year}-${month}-${day}`;
        console.log('Data formatada (ISO com T e Z):', formatted);
        return formatted;
      }
    }
    
    // Se a data já está no formato YYYY-MM-DD
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      console.log('Data já está no formato correto:', dateString);
      return dateString;
    }
    
    // Para outros formatos, tentar converter
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formatted = `${year}-${month}-${day}`;
      console.log('Data formatada (conversão genérica):', formatted);
      return formatted;
    }
    
    console.log('Data não pôde ser formatada, retornando vazio');
    return '';
  } catch (error) {
    console.warn('Erro ao formatar data para input:', dateString, error);
    return '';
  }
};

const ReceitaEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contasOptions, setContasOptions] = useState([]);
  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    dataBalanco: '',
    pedido: '',
    conta: '',
    quantidade: '1'
  });
  const [errors, setErrors] = useState({});

  // Carregar dados da receita e opções
  useEffect(() => {
    const loadData = async () => {
      try {
        setInitialLoading(true);
        const [receita, contas] = await Promise.all([
          fetchReceitaById(id),
          fetchContasReceita()
        ]);

        console.log('Receita carregada:', receita);
        console.log('Data balanço original:', receita.dataBalanco);
        console.log('Data balanço formatada:', formatDateForInput(receita.dataBalanco));
        console.log('Contas carregadas:', contas);
        
        setFormData({
          descricao: receita.descricao || '',
          valor: receita.valor?.toString() || '',
          dataBalanco: formatDateForInput(receita.dataBalanco) || '',
          pedido: receita.pedido?.toString() || '',
          conta: receita.conta || '',
          quantidade: receita.quantidade?.toString() || '1'
        });
        
        // Forçar atualização do campo dataBalanco
        setTimeout(() => {
          console.log('Aplicando data formatada ao campo dataBalanco');
          const formattedDate = formatDateForInput(receita.dataBalanco);
          setFormData(prev => ({
            ...prev,
            dataBalanco: formattedDate
          }));
          console.log('Data aplicada ao campo:', formattedDate);
        }, 100);
        
        // Segundo setTimeout para garantir que o input seja atualizado
        setTimeout(() => {
          console.log('Verificação final do campo dataBalanco');
          const formattedDate = formatDateForInput(receita.dataBalanco);
          setFormData(prev => ({
            ...prev,
            dataBalanco: formattedDate
          }));
          console.log('Data final aplicada:', formattedDate);
        }, 200);
        
        setContasOptions(contas);
      } catch (err) {
        setErrors({ 
          load: err.message || 'Erro ao carregar receita' 
        });
      } finally {
        setInitialLoading(false);
      }
    };
    
    loadData();
  }, [id]);

  // Debug: monitorar mudanças no formData
  useEffect(() => {
    console.log('FormData atualizado:', formData);
    console.log('Campo dataBalanco atual:', formData.dataBalanco);
  }, [formData]);

  // Debug específico para o campo dataBalanco
  useEffect(() => {
    console.log('Campo dataBalanco mudou para:', formData.dataBalanco);
  }, [formData.dataBalanco]);

  // Garantir que a data seja aplicada após o carregamento inicial
  useEffect(() => {
    if (!initialLoading && formData.dataBalanco) {
      console.log('Carregamento inicial concluído, verificando dataBalanco:', formData.dataBalanco);
      // Forçar uma atualização final do campo dataBalanco
      setTimeout(() => {
        setFormData(prev => ({
          ...prev,
          dataBalanco: prev.dataBalanco
        }));
        console.log('Atualização final do campo dataBalanco aplicada');
      }, 50);
    }
  }, [initialLoading, formData.dataBalanco]);

  const handleInputChange = (field, value) => {
    console.log(`Alterando campo ${field}:`, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.valor) {
      newErrors.valor = 'Valor é obrigatório';
    } else if (parseFloat(formData.valor) <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    if (!formData.dataBalanco) {
      newErrors.dataBalanco = 'Data do balanço é obrigatória';
    }

    if (!String(formData.conta).trim()) {
      newErrors.conta = 'Conta é obrigatória';
    }

    if (!formData.quantidade) {
      newErrors.quantidade = 'Quantidade é obrigatória';
    } else if (parseInt(formData.quantidade) <= 0) {
      newErrors.quantidade = 'Quantidade deve ser maior que zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const receitaData = {
        ...formData,
        valor: parseFloat(formData.valor),
        quantidade: parseInt(formData.quantidade),
        pedido: formData.pedido ? parseInt(formData.pedido) : null,
        conta: formData.conta ? parseInt(formData.conta) : null
      };

      await updateReceita(id, receitaData);
      navigate('/financeiro/receitas', { 
        state: { 
          message: 'Receita atualizada com sucesso!', 
          type: 'success' 
        } 
      });
    } catch (err) {
      setErrors({ 
        submit: err.message || 'Erro ao atualizar receita' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/financeiro/receitas');
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (deleteSuccess) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center">
            <div className="text-green-600 text-xl font-semibold">Receita excluída com sucesso!</div>
            <p className="text-gray-600 mt-2">Redirecionando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (errors.load) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {errors.load}
          </div>
          <div className="mt-4">
            <button
              onClick={handleCancel}
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              ← Voltar para lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <div className="flex items-center mb-4">
        <button
          onClick={handleCancel}
          className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} size="lg" />
        </button>
        <h2 className="text-xl font-bold">Editar Receita</h2>
      </div>
      {/* `noValidate`: a validação em português deste formulário é a que o
          usuário deve ver — o balão nativo do navegador some sozinho, sai no
          idioma do navegador e mostra um campo por vez. */}


      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {errors.submit && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {errors.submit}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="descricao" className="block text-sm font-medium mb-1">
              Descrição *
            </label>
            <input
              type="text"
              id="descricao"
              value={formData.descricao}
              onChange={(e) => handleInputChange('descricao', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.descricao ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ex: Venda de Cursos Online"
            />
            {errors.descricao && (
              <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
            )}
          </div>

          <div>
            <label htmlFor="valor" className="block text-sm font-medium mb-1">
              Valor *
            </label>
            <input
              type="number"
              id="valor"
              step="0.01"
              min="0"
              value={formData.valor}
              onChange={(e) => handleInputChange('valor', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.valor ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0,00"
            />
            {errors.valor && (
              <p className="mt-1 text-sm text-red-600">{errors.valor}</p>
            )}
          </div>

          <div>
            <label htmlFor="quantidade" className="block text-sm font-medium mb-1">
              Quantidade *
            </label>
            <input
              type="number"
              id="quantidade"
              min="1"
              value={formData.quantidade}
              onChange={(e) => handleInputChange('quantidade', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.quantidade ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="1"
            />
            {errors.quantidade && (
              <p className="mt-1 text-sm text-red-600">{errors.quantidade}</p>
            )}
          </div>

          <div>
            <label htmlFor="dataBalanco" className="block text-sm font-medium mb-1">
              Data do Balanço *
            </label>
            {console.log('Renderizando dataBalanco:', formData.dataBalanco)}
            <input
              key={`dataBalanco-${formData.dataBalanco}`} // Forçar re-renderização quando a data muda
              type="date"
              id="dataBalanco"
              value={formData.dataBalanco || ''}
              onChange={(e) => handleInputChange('dataBalanco', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.dataBalanco ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.dataBalanco && (
              <p className="mt-1 text-sm text-red-600">{errors.dataBalanco}</p>
            )}
          </div>

          <div>
            <label htmlFor="pedido" className="block text-sm font-medium mb-1">
              Número do Pedido
            </label>
            <input
              type="number"
              id="pedido"
              value={formData.pedido}
              onChange={(e) => handleInputChange('pedido', e.target.value)}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Ex: 1001"
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="conta" className="block text-sm font-medium mb-1">
              Conta *
            </label>
            <div className="relative">
              <select
                id="conta"
                value={formData.conta}
                onChange={(e) => handleInputChange('conta', e.target.value)}
                className={`w-full border rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  errors.conta ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{contasOptions.length === 0 ? 'Nenhuma conta disponível' : 'Selecione uma conta'}</option>
                {contasOptions.length > 0 && contasOptions.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.descricao || conta.nome || `Conta ${conta.id}`}
                  </option>
                ))}
              </select>
            </div>
            {errors.conta && (
              <p className="mt-1 text-sm text-red-600">{errors.conta}</p>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button
            type="button"
            onClick={handleCancel}
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
            disabled={loading || deleting}
           aria-label="Cancelar">
            <FontAwesomeIcon icon={faArrowLeft} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 w-10 sm:w-auto justify-center"
            disabled={loading || deleting}
           aria-label="Excluir">
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
          </button>
          <button
            type="submit"
            disabled={loading || deleting}
            className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
           aria-label="Salvar">
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSave} />
            )}
            <span className="hidden sm:inline">
              {loading ? 'Salvando...' : 'Salvar'}
            </span>
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirmar Exclusão"
        message="Deseja realmente excluir esta receita? Esta ação não poderá ser desfeita."
        confirmType="delete"
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          setDeleting(true);
          setErrors({});
          
          try {
            await deleteReceita(id);
            setDeleteSuccess(true);
            setTimeout(() => navigate('/financeiro/receitas'), 1200);
          } catch (err) {
            setErrors({ 
              submit: 'Erro ao excluir receita: ' + err.message 
            });
            setDeleting(false);
          }
        }}
        danger
      />
    </div>
  );
};

export default ReceitaEdit;
